import type { Polygon2D } from '../game/Collision';

export const DEFAULT_TRACK_WORLD_SCALE = 1.18;
// Centerline-only shrink applied on top of worldScale. Kept at 1.0 by
// default — the arcade-flavored CSVs come pre-compressed by the offline
// generator (see tools/generate-track.mjs). Lower values shrink an
// already-arcade CSV further at the cost of tightening corner radii.
export const DEFAULT_CENTERLINE_LENGTH_SCALE = 1.0;

export interface CenterlinePoint {
  x: number;
  z: number;
  widthLeft: number;
  widthRight: number;
}

export interface Track {
  centerline: CenterlinePoint[];
  /** Tangent (forward) at each centerline point — unit vector in (x, z). */
  tangents: { tx: number; tz: number }[];
  /**
   * Subtle road-bank in radians at each centerline point. Positive lifts the
   * outer edge above the inner edge (proper banked turn); zero on straights.
   * Used by the renderer to tilt the track surface and by the racer to roll
   * the pod with the surface.
   */
  bankRadians: number[];
  /**
   * The drivable surface is the *ring* between innerPolygon and outerPolygon.
   * A point is "on track" iff it is inside outerPolygon AND outside innerPolygon.
   * The walls are the same idea offset by `wallBuffer`.
   */
  innerPolygon: Polygon2D;
  outerPolygon: Polygon2D;
  wallInnerPolygon: Polygon2D;
  wallOuterPolygon: Polygon2D;
  /** Approximate centroid of the track (used as inward direction reference). */
  centroid: { x: number; z: number };
  wallBuffer: number;
  /** Cumulative arc length along centerline at each point. */
  cumulativeLength: number[];
  totalLength: number;
  worldScale: number;
}

export interface TrackPosition {
  segmentIndex: number;
  t: number;
  closestX: number;
  closestZ: number;
  tx: number;
  tz: number;
  nx: number;
  nz: number;
  signedLateral: number;
  widthLeft: number;
  widthRight: number;
  arcLength: number;
  distance: number;
}

/**
 * Default wallBuffer aligns the (invisible) corridor wall with the visible
 * rail/fence/wall barriers. `TrackFurniture.collisionLineFor` places those at
 * `widthLeft + maxKerbOuterWidth + extraOffset ≈ widthLeft + 9.30 m` in world
 * space (post-`worldScale`). To put the corridor wall at the same world
 * offset we need `wallBuffer * worldScale ≈ 9.30`, i.e. `wallBuffer ≈ 7.88`
 * for the default `worldScale = 1.18`. With this default the pod stops *at*
 * the visible barrier instead of several metres short of it (the "invisible
 * barrier" bug — see FenceCollision.test.ts).
 */
export const DEFAULT_WALL_BUFFER = 7.88;

export async function loadTrack(url: string, wallBuffer = DEFAULT_WALL_BUFFER): Promise<Track> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to load track CSV ${url}: ${res.status}`);
  const text = await res.text();
  const points = parseCsv(text);
  if (points.length < 8) throw new Error(`Track CSV has too few points (${points.length})`);
  return buildTrack(points, wallBuffer, DEFAULT_TRACK_WORLD_SCALE, DEFAULT_CENTERLINE_LENGTH_SCALE);
}

export function buildTrackFromCenterline(sourceCenterline: CenterlinePoint[], wallBuffer = DEFAULT_WALL_BUFFER): Track {
  return buildTrack(sourceCenterline, wallBuffer, 1, 1);
}

function parseCsv(text: string): CenterlinePoint[] {
  const out: CenterlinePoint[] = [];
  const lines = text.split(/\r?\n/);
  for (const line of lines) {
    const t = line.trim();
    if (!t || t.startsWith('#')) continue;
    const parts = t.split(',').map((s) => s.trim());
    if (parts.length < 4) continue;
    const x = Number(parts[0]);
    const y = Number(parts[1]);
    const wr = Number(parts[2]);
    const wl = Number(parts[3]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    out.push({ x, z: y, widthRight: wr, widthLeft: wl });
  }
  return out;
}

function buildTrack(
  sourceCenterline: CenterlinePoint[],
  wallBuffer: number,
  worldScale = DEFAULT_TRACK_WORLD_SCALE,
  centerlineLengthScale = DEFAULT_CENTERLINE_LENGTH_SCALE,
): Track {
  const origin = sourceCenterline[0];
  const positionScale = worldScale * centerlineLengthScale;
  const centerline = sourceCenterline.map((p) => ({
    x: origin.x + (p.x - origin.x) * positionScale,
    z: origin.z + (p.z - origin.z) * positionScale,
    widthLeft: p.widthLeft * worldScale,
    widthRight: p.widthRight * worldScale,
  }));
  const scaledWallBuffer = wallBuffer * worldScale;
  const n = centerline.length;

  // Rotate the whole track so the start tangent is +Z. This way pressing W at
  // the start moves the racer "up the screen" in a fixed-world camera setup.
  const rawA = centerline[n - 1];
  const rawB = centerline[1];
  const startHeading = Math.atan2(rawB.x - rawA.x, rawB.z - rawA.z);
  if (Math.abs(startHeading) > 1e-4) {
    const c = Math.cos(startHeading);
    const s = Math.sin(startHeading);
    const sx = centerline[0].x;
    const sz = centerline[0].z;
    for (const p of centerline) {
      const dx = p.x - sx;
      const dz = p.z - sz;
      p.x = sx + dx * c - dz * s;
      p.z = sz + dx * s + dz * c;
    }
  }

  const tangents: { tx: number; tz: number }[] = new Array(n);
  for (let i = 0; i < n; i++) {
    const a = centerline[(i - 1 + n) % n];
    const b = centerline[(i + 1) % n];
    let tx = b.x - a.x;
    let tz = b.z - a.z;
    const len = Math.hypot(tx, tz) || 1;
    tx /= len; tz /= len;
    tangents[i] = { tx, tz };
  }

  const bankRadians = computeBankRadians(tangents);

  const innerPolygon: Polygon2D = new Array(n);
  const outerPolygon: Polygon2D = new Array(n);
  const wallInnerPolygon: Polygon2D = new Array(n);
  const wallOuterPolygon: Polygon2D = new Array(n);

  for (let i = 0; i < n; i++) {
    const p = centerline[i];
    const { tx, tz } = tangents[i];
    const nx = -tz;
    const nz = tx;
    innerPolygon[i] = { x: p.x + nx * p.widthLeft, z: p.z + nz * p.widthLeft };
    outerPolygon[i] = { x: p.x - nx * p.widthRight, z: p.z - nz * p.widthRight };
    wallInnerPolygon[i] = {
      x: p.x + nx * (p.widthLeft + scaledWallBuffer),
      z: p.z + nz * (p.widthLeft + scaledWallBuffer),
    };
    wallOuterPolygon[i] = {
      x: p.x - nx * (p.widthRight + scaledWallBuffer),
      z: p.z - nz * (p.widthRight + scaledWallBuffer),
    };
  }

  let cx = 0, cz = 0;
  for (const p of centerline) { cx += p.x; cz += p.z; }
  cx /= n; cz /= n;

  const cumulative: number[] = new Array(n);
  cumulative[0] = 0;
  for (let i = 1; i < n; i++) {
    const a = centerline[i - 1];
    const b = centerline[i];
    cumulative[i] = cumulative[i - 1] + Math.hypot(b.x - a.x, b.z - a.z);
  }
  const last = centerline[n - 1];
  const first = centerline[0];
  const totalLength = cumulative[n - 1] + Math.hypot(first.x - last.x, first.z - last.z);

  return {
    centerline,
    tangents,
    bankRadians,
    innerPolygon,
    outerPolygon,
    wallInnerPolygon,
    wallOuterPolygon,
    centroid: { x: cx, z: cz },
    wallBuffer: scaledWallBuffer,
    cumulativeLength: cumulative,
    totalLength,
    worldScale,
  };
}

/**
 * Locate a world-space point relative to the nearest centerline segment.
 *
 * This is the authoritative drivable corridor model. The track may visually
 * cross over itself, so global polygon containment is not reliable for physics
 * or timing; local corridor sampling is.
 */
export function locateTrackPosition(track: Track, x: number, z: number): TrackPosition {
  let bestSegment = 0;
  let bestT = 0;
  let bestClosestX = 0;
  let bestClosestZ = 0;
  let bestTx = 0;
  let bestTz = 1;
  let bestLen = 1;
  let bestDistSq = Infinity;

  const n = track.centerline.length;
  for (let i = 0; i < n; i++) {
    const a = track.centerline[i];
    const b = track.centerline[(i + 1) % n];
    const sx = b.x - a.x;
    const sz = b.z - a.z;
    const lenSq = sx * sx + sz * sz || 1e-9;
    const t = Math.max(0, Math.min(1, ((x - a.x) * sx + (z - a.z) * sz) / lenSq));
    const cx = a.x + sx * t;
    const cz = a.z + sz * t;
    const dx = x - cx;
    const dz = z - cz;
    const distSq = dx * dx + dz * dz;
    if (distSq < bestDistSq) {
      bestDistSq = distSq;
      bestSegment = i;
      bestT = t;
      bestClosestX = cx;
      bestClosestZ = cz;
      bestLen = Math.sqrt(lenSq);
      bestTx = sx / bestLen;
      bestTz = sz / bestLen;
    }
  }

  const a = track.centerline[bestSegment];
  const b = track.centerline[(bestSegment + 1) % n];
  const nx = -bestTz;
  const nz = bestTx;
  const dx = x - bestClosestX;
  const dz = z - bestClosestZ;
  const arcBase = track.cumulativeLength[bestSegment];
  const arcLength = Math.min(track.totalLength, arcBase + bestLen * bestT);

  return {
    segmentIndex: bestSegment,
    t: bestT,
    closestX: bestClosestX,
    closestZ: bestClosestZ,
    tx: bestTx,
    tz: bestTz,
    nx,
    nz,
    signedLateral: dx * nx + dz * nz,
    widthLeft: lerp(a.widthLeft, b.widthLeft, bestT),
    widthRight: lerp(a.widthRight, b.widthRight, bestT),
    arcLength,
    distance: Math.sqrt(bestDistSq),
  };
}

export function sampleCenterlineAhead(
  track: Track,
  fromArcLength: number,
  distance: number,
): { x: number; z: number; tx: number; tz: number } {
  const total = Math.max(track.totalLength, 1e-6);
  const target = ((fromArcLength + distance) % total + total) % total;
  const cumulative = track.cumulativeLength;
  let lo = 0;
  let hi = cumulative.length - 1;
  while (lo < hi) {
    const mid = Math.floor((lo + hi + 1) / 2);
    if (cumulative[mid] <= target) lo = mid;
    else hi = mid - 1;
  }

  const i = lo;
  const next = (i + 1) % track.centerline.length;
  const segmentStart = cumulative[i];
  const segmentEnd = next === 0 ? total : cumulative[next];
  const t = (target - segmentStart) / Math.max(1e-6, segmentEnd - segmentStart);
  const a = track.centerline[i];
  const b = track.centerline[next];
  const ta = track.tangents[i];
  const tb = track.tangents[next];
  let tx = lerp(ta.tx, tb.tx, t);
  let tz = lerp(ta.tz, tb.tz, t);
  const tangentLen = Math.hypot(tx, tz) || 1;
  tx /= tangentLen;
  tz /= tangentLen;
  return {
    x: lerp(a.x, b.x, t),
    z: lerp(a.z, b.z, t),
    tx,
    tz,
  };
}

/** A point is on the drivable surface if it lies in the local centerline corridor. */
export function isOnTrack(track: Track, x: number, z: number): boolean {
  const pos = locateTrackPosition(track, x, z);
  return pos.signedLateral <= pos.widthLeft + 1e-6
    && pos.signedLateral >= -pos.widthRight - 1e-6;
}

/** A point is inside the racing corridor (between buffered walls). */
export function isInsideWalls(track: Track, x: number, z: number): boolean {
  const pos = locateTrackPosition(track, x, z);
  return pos.signedLateral <= pos.widthLeft + track.wallBuffer + 1e-6
    && pos.signedLateral >= -pos.widthRight - track.wallBuffer - 1e-6;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

const MAX_BANK_RADIANS = (4 * Math.PI) / 180;
const BANK_PER_CURVATURE = 1.8;

function computeBankRadians(tangents: { tx: number; tz: number }[]): number[] {
  const n = tangents.length;
  const raw = new Array<number>(n);
  for (let i = 0; i < n; i++) {
    const a = tangents[(i - 1 + n) % n];
    const b = tangents[(i + 1) % n];
    const cross = a.tx * b.tz - a.tz * b.tx;
    let bank = cross * BANK_PER_CURVATURE;
    if (bank > MAX_BANK_RADIANS) bank = MAX_BANK_RADIANS;
    else if (bank < -MAX_BANK_RADIANS) bank = -MAX_BANK_RADIANS;
    raw[i] = bank;
  }
  let smoothed = raw;
  for (let pass = 0; pass < 3; pass++) {
    const next = new Array<number>(n);
    for (let i = 0; i < n; i++) {
      const a = smoothed[(i - 1 + n) % n];
      const b = smoothed[i];
      const c = smoothed[(i + 1) % n];
      next[i] = (a + b + c) / 3;
    }
    smoothed = next;
  }
  return smoothed;
}

/** Test-only re-export of the polygon construction so node tests can build a Track without fetch(). */
export const __test__buildTrack = buildTrack;

/**
 * Pose for the racer's spawn. `rollbackMeters` shifts the spawn backwards
 * along the track tangent so the player crosses the start line with
 * momentum already built up — the lap timer is gated on the first start-line
 * crossing so that wind-up time isn't charged to lap one.
 */
export function getStartPose(
  track: Track,
  rollbackMeters: number = 0,
): { x: number; z: number; heading: number } {
  const i = 0;
  const p = track.centerline[i];
  const { tx, tz } = track.tangents[i];
  const heading = Math.atan2(tx, tz);
  return {
    x: p.x - tx * rollbackMeters,
    z: p.z - tz * rollbackMeters,
    heading,
  };
}
